import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import ScModal from './ScModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Sc {
  sc_id: number;
  sc_name: string;
  sc_status: number;
}

const Sc: React.FC = () => {
  const [scs, setScs] = useState<Sc[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Sc | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchSc = async () => {
    try {
      const data = await apiRequest<Sc[]>({
        method: 'get',
        url: 'sub-colors',
      });
      setScs(data);
    } catch (error) {
      showError('Failed to fetch Sub Colors');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchSc();
  }, []);

  const handleEditClick = (record: Sc) => {
    setIsEditMode(true);
    setInitialValues({
      sc_id: record.sc_id,
      sc_name: record.sc_name,
      sc_status: record.sc_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchSc();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (sc_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-sub-color/${sc_id}`,
      });
      showSuccess('Sub Color deleted');
      fetchSc();
    } catch {
      showError('Failed to delete Sub Color');
    }
  };

  const handleStatusToggle = async (scId: number) => {
    setScs((prev) =>
      prev.map((item) =>
        item.sc_id === scId
          ? { ...item, sc_status: item.sc_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-sub-color/${scId}`,
      });
      showSuccess('Sub Color status updated');
    } catch (error) {
      showError('Failed to update status');
      setScs((prev) =>
        prev.map((item) =>
          item.sc_id === scId
            ? { ...item, sc_status: item.sc_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Sc>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Sc> = [
    {
      title: 'Sub Color',
      dataIndex: 'sc_name',
      key: 'sc_name',
      filters: Array.from(new Set(scs.map((s) => s.sc_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.sc_name || null,
      onFilter: (value, record) => record.sc_name.includes(value as string),
      sorter: (a, b) => a.sc_name.localeCompare(b.sc_name),
      sortOrder: sortedInfo.columnKey === 'sc_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              backgroundColor: record.sc_name,
              width: 20,
              height: 20,
              borderRadius: 4,
              border: '1px solid #ccc',
            }}
          />
          <span>{record.sc_name}</span>
        </div>
      )
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Sc) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.sc_status}
                onChange={() => handleStatusToggle(record.sc_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this Sub Color?"
              onConfirm={() => handleDelete(record.sc_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Sub Color</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Sub Color
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Sc>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="sc_id"
          dataSource={scs.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No Sub Color found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, scs.length)} of{' '}
                {scs.length}
              </span>
              <Pagination
                {...pagination}
                total={scs.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <ScModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Sc;